import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ClientDTO,
  FilterDTO,
  PagedResultDTOOfParkerCompanyDTO,
  PagedResultDTOOfParkerDTO,
} from '../../../../../base/models/models.swagger.generated';
import { TagsPerParkerReportService } from '../tags-per-parker-report.service';
import { ClientService } from '../../../../../base/services/client/client.service';
import { SearchFieldComponent } from '../../../../../base/shared/search-field/search-field.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAutocompleteItem } from '../../../../../base/shared/autocomplete/autocomplete-item';
import { AutocompleteComponent } from '../../../../../base/shared/autocomplete/autocomplete.component';
import { TranslateModule } from '@ngx-translate/core';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { ClientAutocompleteComponent } from '../../../../../base/shared/client-autocomplete/client-autocomplete.component';

@Component({
    selector: 'app-tags-form',
    templateUrl: './tags-form.component.html',
    styleUrls: ['./tags-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        ClientAutocompleteComponent,
        AutocompleteComponent,
        SearchFieldComponent,
        AppErrorMessagesComponent,
        TranslateModule
    ]
})
export class TagsFormComponent implements OnInit, AfterViewInit, OnDestroy{

  @ViewChild(ClientAutocompleteComponent)
  public clientAutocomplete: ClientAutocompleteComponent;

  @ViewChild('companyAutocomplete')
  public companyAutocomplete: AutocompleteComponent;

  @ViewChild('searchField')
  public searchField: SearchFieldComponent;

  @Input() public form: UntypedFormGroup;

  @Output()
  public searchDTOChanged = new EventEmitter<FilterDTO[]>();

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
    }
    // Defer the event emission to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.onClientSelected(value);
    });
  }

  public get selectedCompanyId(): number {
    if (this.form.get('companyId').value === null) {
      return null;
    }
    return this.form.get('companyId').value;
  }

  public set selectedCompanyId(value: number) {
    //TODO Currently workaround
    //Check if actual record has been selected
    if (typeof value === 'number') {
      this.form.get('companyId').setValue(value);
    } else {
      this.form.get('companyId').setValue(null);
    }
    //Disable company Autocomplete if parker selected
    if(this.form.get('parkerId').value && !this.form.get('companyId').value) {
      this.companyAutocomplete.enabled = false;
    }
  }

  public get selectedParkerId(): number {
    if (this.form.get('parkerId').value === null) {
      return null;
    }
    return this.form.get('parkerId').value;
  }

  public set selectedParkerId(value: number) {
    //TODO Currently workaround
    //Check if actual record has been selected
    if (typeof value === 'number') {
      this.form.get('parkerId').setValue(value);
    } else {
      this.form.get('parkerId').setValue(null);
    }
    //Disable company Autocomplete if parker selected
    if(this.form.get('parkerId').value && !this.form.get('companyId').value) {
      this.companyAutocomplete.enabled = false;
    } else {
      this.companyAutocomplete.enabled = true;
    }
  }

  subscriptions = [];

  constructor(protected clientService: ClientService,
              private tagsPerParkerReportService: TagsPerParkerReportService) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        this.searchDTOChanged.emit();
      }));
  }

  ngAfterViewInit(): void {
    if (!this.selectedClient) {
      this.focusClientAutocomplete();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public onSearchTextChanged(searchText: string): void {
    this.form.get('searchText').setValue(searchText);
  }

  public onClientSelected(client: ClientDTO): void {
    if(client){
      this.form.reset();
      //Specific for search field
      this.searchField?.searchForm?.reset();
      this.searchDTOChanged.emit();
    }
  }

  public searchCompanyDropdownValues(searchText: string): Observable<IAutocompleteItem[]> {
    return this.tagsPerParkerReportService.searchCompanyDropdownValues(searchText)
      .pipe(map((companyDropdownValues: PagedResultDTOOfParkerCompanyDTO) => {
        const data: IAutocompleteItem[] = [];
        companyDropdownValues.items.forEach(customer => {
          data.push({id: customer.id, name: customer.name});
        });
        return data;
      }));
  }

  public searchParkerDropdownValues(searchText: string): Observable<IAutocompleteItem[]> {
    return this.tagsPerParkerReportService.searchParkerDropdownValues(searchText, this.form.get('companyId').value)
      .pipe(map((parkerDropdownValues: PagedResultDTOOfParkerDTO) => {
        const data: IAutocompleteItem[] = [];
        parkerDropdownValues.items.forEach(customer => {
          const fullName = customer.firstName + ' ' + customer.lastName;
          data.push({id: customer.id, name: fullName});
        });
        return data;
      }));
  }

  private focusClientAutocomplete(): void {
    if (this.clientAutocomplete) {
      this.clientAutocomplete.focus();
    }
  }
}
